"use client";

import "./CandidateProf.css";
import {
  Box,
  Button,
  Input,
  Stack,
  Switch,
  useToast,
  Text,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Alert,
  AlertIcon,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useRef, useState } from "react";
import {
  fetchJobEducationAsync,
  selectJobEducation,
} from "../../AdminPanel/JobPostingSlice/JobEducation/JobEducationSlice";
import {
  fetchJobEducationCourseAsync,
  selectJobEducationCourse,
} from "../../AdminPanel/JobPostingSlice/JobEducationCourse/JobEducationCourceSlice";
import {
  addCandidateEducationAsync,
  fetchCandidateEducationAsync,
  selectCandidateEducation,
} from "../CandidateProfileSlice/Education/EducationSlice";
import { useForm, Controller } from "react-hook-form";
import {
  fetchJobEducationCourseSpecialtyAsync,
  selectJobEducationCourseSpecialty,
} from "../../AdminPanel/JobPostingSlice/JobEducationCourseSpecialty/JobEducationCourseSpecialtySlice";
import { ArrowRightIcon, ArrowLeftIcon } from "@chakra-ui/icons";

export default function Education() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const jobEducation = useSelector(selectJobEducation);
  const jobEducationCourse = useSelector(selectJobEducationCourse);
  const specialization = useSelector(selectJobEducationCourseSpecialty);
  const education = useSelector(selectCandidateEducation);

  const [formError, setFormError] = useState("");
  const [isChecking, setIsChecking] = useState(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isLoadingSpecializations, setIsLoadingSpecializations] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const currentYear = new Date().getFullYear();
  const isMountedRef = useRef(true);
  const shouldRedirectRef = useRef(true);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      education_uuid: "",
      course_uuid: "",
      specialization_uuid: "",
      insitute_university: "",
      university_name: "",
      course_type: "Full Time",
      grading_system: "",
      marks: "",
      primary_education: false,
      course_duration_start_year: currentYear,
      course_duration_end_year: currentYear,
    },
  });

  // Watchers for dependent fields
  const selectedDegreeUuid = watch("education_uuid");
  const selectedCourseUuid = watch("course_uuid");
  const selectedStartYear = Number(watch("course_duration_start_year") || currentYear);
  const gradingSystem = watch("grading_system");

  // Year options
  const startYears = React.useMemo(
    () => Array.from({ length: currentYear - 1975 + 1 }, (_, i) => 1975 + i),
    [currentYear]
  );
  const endYears = React.useMemo(
    () =>
      Array.from({ length: currentYear + 5 - 1975 + 1 }, (_, i) => 1975 + i).filter(
        (year) => year >= selectedStartYear
      ),
    [currentYear, selectedStartYear]
  );

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    const initializeForm = async () => {
      try {
        await Promise.all([
          dispatch(fetchCandidateEducationAsync()),
          dispatch(fetchJobEducationAsync()),
        ]);
      } catch (error) {
        toast({
          title: "Error loading form data",
          description: "Failed to load education options. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } finally {
        if (isMountedRef.current) {
          setIsChecking(false);
        }
      }
    };

    initializeForm();
  }, [dispatch, toast]);

  // Handle redirect decision
  useEffect(() => {
    if (isChecking || education === null) return;

    // If coming from previous page with saved data, don't redirect
    if (location.state?.fromSave) {
      shouldRedirectRef.current = false;
      return;
    }

    // If education data exists and we should redirect, go to dashboard
    if (
      shouldRedirectRef.current &&
      Array.isArray(education) &&
      education.length > 0
    ) {
      navigate("/candidate-dashboard", { replace: true });
    }
  }, [education, isChecking, navigate, location.state]);

  // Prefill form data when coming from another page
  useEffect(() => {
    const formData = location.state?.formData;
    if (!formData) return;

    const loadDependentData = async () => {
      try {
        const promises = [];

        if (formData.education_uuid) {
          promises.push(
            dispatch(fetchJobEducationCourseAsync(formData.education_uuid))
          );
        }

        if (formData.course_uuid) {
          promises.push(
            dispatch(fetchJobEducationCourseSpecialtyAsync(formData.course_uuid))
          );
        }

        await Promise.all(promises);

        // Reset form with prefilled data
        reset({
          education_uuid: formData.education_uuid || "",
          course_uuid: formData.course_uuid || "",
          specialization_uuid: formData.specialization_uuid || "",
          insitute_university: formData.insitute_university || "",
          university_name: formData.university_name || "",
          course_type: formData.course_type || "Full Time",
          grading_system: formData.grading_system || "",
          marks: formData.marks || "",
          primary_education:
            typeof formData.primary_education === "boolean"
              ? formData.primary_education
              : false,
          course_duration_start_year:
            Number(formData.course_duration_start_year) || currentYear,
          course_duration_end_year:
            Number(formData.course_duration_end_year) || currentYear,
        });
      } catch (error) {
        console.error("Error loading dependent data:", error);
      }
    };

    loadDependentData();
  }, [location.state?.formData, reset, dispatch, currentYear]);

  // Fetch courses when degree changes
  useEffect(() => {
    if (!selectedDegreeUuid) return;

    const loadCourses = async () => {
      // Reset dependent fields
      setValue("course_uuid", "");
      setValue("specialization_uuid", "");
      setIsLoadingCourses(true);

      try {
        await dispatch(fetchJobEducationCourseAsync(selectedDegreeUuid));

        if (isMountedRef.current) {
          toast({
            title: "Courses loaded",
            description: "Please select your course",
            status: "info",
            duration: 2000,
            isClosable: true,
            position: "top-right",
          });
        }
      } catch (error) {
        if (isMountedRef.current) {
          toast({
            title: "Error loading courses",
            description: "Failed to load courses. Please try selecting the degree again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingCourses(false);
        }
      }
    };

    loadCourses();
  }, [selectedDegreeUuid, dispatch, setValue, toast]);

  // Fetch specializations when course changes
  useEffect(() => {
    if (!selectedCourseUuid) return;

    const loadSpecializations = async () => {
      setValue("specialization_uuid", "");
      setIsLoadingSpecializations(true);

      try {
        await dispatch(fetchJobEducationCourseSpecialtyAsync(selectedCourseUuid));
      } catch (error) {
        if (isMountedRef.current) {
          toast({
            title: "Error loading specializations",
            description: "Failed to load specializations. Please try selecting the course again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingSpecializations(false);
        }
      }
    };

    loadSpecializations();
  }, [selectedCourseUuid, dispatch, setValue, toast]);

  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
  }, [isDirty]);

  const validateMarks = (value) => {
    if (value === undefined || value === null || value === "") {
      return "Marks are required";
    }

    const numValue = Number.parseFloat(value);
    if (Number.isNaN(numValue)) return "Please enter a valid number";

    if (numValue < 0) return "Marks cannot be negative";

    if (gradingSystem === "CGPA") {
      if (numValue > 10) return "CGPA must be between 0 and 10";
    } else if (gradingSystem === "GPA") {
      if (numValue > 4) return "GPA must be between 0 and 4";
    } else if (gradingSystem === "Percentage") {
      if (numValue > 100) return "Percentage must be between 0 and 100";
    } else {
      return "Please select a grading system first";
    }

    return true;
  };

  const handleSave = async (formValues) => {
    setFormError("");

    try {
      const response = await dispatch(
        addCandidateEducationAsync({ newCandidateEducation: formValues })
      );

      if (!isMountedRef.current) return;

      if (response?.payload?.status === "success") {
        shouldRedirectRef.current = false;

        toast({
          title: "Details saved",
          description: "Your education information has been successfully saved.",
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });

        // Navigate to skills page
        navigate("/candidate-profile/skills", {
          state: { formData: formValues, fromSave: true },
          replace: true,
        });

        // Refresh education list in background
        dispatch(fetchCandidateEducationAsync());

        setHasUnsavedChanges(false);
      } else {
        setFormError(
          "Could not save education details. Please check your information and try again."
        );
        toast({
          title: "Error saving details",
          description:
            "There was an error saving your information. Please check all fields and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      setFormError(
        error?.message || "An unexpected error occurred. Please try again."
      );
      toast({
        title: "Error saving details",
        description:
          error?.message ||
          "There was an error saving your information. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handlePrevious = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to go back?"
      );
      if (!confirmLeave) return;
    }

    const currentFormData = watch();
    navigate("/candidate-profile/", {
      state: { formData: currentFormData },
    });
  };

  const scrollToFirstError = () => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const element = document.getElementsByName(firstErrorField)[0];
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
      element?.focus();
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      scrollToFirstError();
    }
  }, [errors]);

  if (isChecking) {
    return (
      <Box w="100%" py={10} textAlign="center">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
        <Text mt={4} fontSize="lg" color="gray.600">
          Loading education form...
        </Text>
      </Box>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(handleSave)}>
        <div className="flex w-full justify-between">
          <div className="w-full Edu-right-box">
            <div className="w-full px-10 py-3">
              <h1 className="text-2xl text-start mb-4">Education</h1>

              {formError && (
                <Alert status="error" mt={4} mb={4} borderRadius="md">
                  <AlertIcon />
                  {formError}
                </Alert>
              )}

              {/* Degree Select */}
              <FormControl isInvalid={!!errors.education_uuid} mt={4} isRequired>
                <FormLabel fontWeight="semibold">Degree</FormLabel>
                <Select
                  placeholder="Select Degree"
                  {...register("education_uuid", {
                    required: "Degree is required",
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.education_uuid ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.education_uuid ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                  autoFocus
                >
                  {Array.isArray(jobEducation) &&
                    jobEducation.map((degree) => (
                      <option key={degree.uuid} value={degree.uuid}>
                        {degree.title}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage>
                  {errors.education_uuid && errors.education_uuid.message}
                </FormErrorMessage>
              </FormControl>

              {/* Course Select */}
              <FormControl isInvalid={!!errors.course_uuid} mt={4} isRequired>
                <FormLabel fontWeight="semibold">
                  Education Course
                  {isLoadingCourses && (
                    <Spinner size="sm" ml={2} color="blue.500" />
                  )}
                </FormLabel>
                <Select
                  placeholder={
                    !selectedDegreeUuid
                      ? "Select a degree first"
                      : isLoadingCourses
                        ? "Loading courses..."
                        : "Select Course"
                  }
                  {...register("course_uuid", {
                    required: "Education Course is required",
                  })}
                  isDisabled={!selectedDegreeUuid || isLoadingCourses}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.course_uuid ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.course_uuid ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  {Array.isArray(jobEducationCourse) &&
                    jobEducationCourse.map((course) => (
                      <option key={course.uuid} value={course.uuid}>
                        {course.title}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage>
                  {errors.course_uuid && errors.course_uuid.message}
                </FormErrorMessage>
                {!selectedDegreeUuid && (
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Please select a degree first
                  </Text>
                )}
              </FormControl>

              {/* Specialization Select */}
              <FormControl isInvalid={!!errors.specialization_uuid} mt={4}>
                <FormLabel fontWeight="semibold">
                  Specialization <Text as="span" color="gray.500">(Optional)</Text>
                  {isLoadingSpecializations && (
                    <Spinner size="sm" ml={2} color="blue.500" />
                  )}
                </FormLabel>
                <Select
                  placeholder={
                    !selectedCourseUuid
                      ? "Select a course first"
                      : isLoadingSpecializations
                        ? "Loading specializations..."
                        : "Select Specialization"
                  }
                  {...register("specialization_uuid")}
                  isDisabled={!selectedCourseUuid || isLoadingSpecializations}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.specialization_uuid ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.specialization_uuid ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  {Array.isArray(specialization) &&
                    specialization?.map((specialty) => (
                      <option key={specialty.uuid} value={specialty.uuid}>
                        {specialty.title}
                      </option>
                    ))}
                </Select>
                <FormErrorMessage>
                  {errors.specialization_uuid && errors.specialization_uuid.message}
                </FormErrorMessage>
                {!selectedCourseUuid && (
                  <Text color="gray.500" fontSize="sm" mt={1}>
                    Please select a course first
                  </Text>
                )}
              </FormControl>

              {/* Institute Name */}
              <FormControl isInvalid={!!errors.insitute_university} mt={4} isRequired>
                <FormLabel fontWeight="semibold">Institute Name</FormLabel>
                <Input
                  type="text"
                  placeholder="Enter Institute Name (e.g., ABC College)"
                  {...register("insitute_university", {
                    required: "Institute name is required",
                    minLength: {
                      value: 3,
                      message: "Institute name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Institute name cannot exceed 100 characters",
                    },
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.insitute_university ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.insitute_university ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                />
                <FormErrorMessage>
                  {errors.insitute_university && errors.insitute_university.message}
                </FormErrorMessage>
              </FormControl>

              {/* University Name */}
              <FormControl isInvalid={!!errors.university_name} mt={4} isRequired>
                <FormLabel fontWeight="semibold">University Name</FormLabel>
                <Input
                  type="text"
                  placeholder="Enter University Name (e.g., XYZ University)"
                  {...register("university_name", {
                    required: "University name is required",
                    minLength: {
                      value: 3,
                      message: "University name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "University name cannot exceed 100 characters",
                    },
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.university_name ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.university_name ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                />
                <FormErrorMessage>
                  {errors.university_name && errors.university_name.message}
                </FormErrorMessage>
              </FormControl>

              {/* Course Type */}
              <FormControl isInvalid={!!errors.course_type} mt={4} isRequired>
                <FormLabel fontWeight="semibold">Course Type</FormLabel>
                <Select
                  {...register("course_type", {
                    required: "Course type is required",
                  })}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor={errors.course_type ? "red.500" : "gray.200"}
                  _focus={{
                    borderColor: errors.course_type ? "red.500" : "blue.500",
                    boxShadow: "0 0 0 1px",
                  }}
                >
                  <option value="Full Time">Full Time</option>
                  <option value="Part Time">Part Time</option>
                  <option value="Distance Learning">Distance Learning</option>
                </Select>
                <FormErrorMessage>
                  {errors.course_type && errors.course_type.message}
                </FormErrorMessage>
              </FormControl>

              {/* Grading + Marks */}
              <Stack direction={{ base: "column", md: "row" }} spacing={4} mt={4}>
                <FormControl
                  isInvalid={!!errors.grading_system}
                  isRequired
                  width={{ base: "100%", md: "50%" }}
                >
                  <FormLabel fontWeight="semibold">Grading System</FormLabel>
                  <Select
                    placeholder="Select Grading System"
                    {...register("grading_system", {
                      required: "Grading system is required",
                      onChange: () => {
                        setValue("marks", "");
                        trigger("marks");
                      },
                    })}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={errors.grading_system ? "red.500" : "gray.200"}
                    _focus={{
                      borderColor: errors.grading_system ? "red.500" : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  >
                    <option value="CGPA">CGPA (0-10)</option>
                    <option value="Percentage">Percentage (0-100)</option>
                    <option value="GPA">GPA (0-4)</option>
                  </Select>
                  <FormErrorMessage>
                    {errors.grading_system && errors.grading_system.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.marks} isRequired flex="1">
                  <FormLabel fontWeight="semibold">Your Score</FormLabel>
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    max={
                      gradingSystem === "CGPA"
                        ? 10
                        : gradingSystem === "GPA"
                          ? 4
                          : gradingSystem === "Percentage"
                            ? 100
                            : undefined
                    }
                    placeholder={
                      gradingSystem === "CGPA"
                        ? "Enter CGPA (0-10)"
                        : gradingSystem === "GPA"
                          ? "Enter GPA (0-4)"
                          : gradingSystem === "Percentage"
                            ? "Enter Percentage (0-100)"
                            : "Select grading system first"
                    }
                    {...register("marks", {
                      required: "Marks are required",
                      validate: validateMarks,
                    })}
                    isDisabled={!gradingSystem}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={errors.marks ? "red.500" : "gray.200"}
                    _focus={{
                      borderColor: errors.marks ? "red.500" : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  />
                  <FormErrorMessage>
                    {errors.marks && errors.marks.message}
                  </FormErrorMessage>
                  {!gradingSystem && (
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      Please select a grading system first
                    </Text>
                  )}
                </FormControl>
              </Stack>

              {/* Primary Education */}
              <FormControl mt={4}>
                <Stack direction="row" alignItems="center">
                  <FormLabel fontWeight="semibold" mb={0}>
                    Is this your primary/highest education?
                  </FormLabel>
                  <Controller
                    name="primary_education"
                    control={control}
                    render={({ field: { value, onChange, ref } }) => (
                      <Switch
                        colorScheme="teal"
                        size="lg"
                        isChecked={!!value}
                        onChange={(e) => onChange(e.target.checked)}
                        ref={ref}
                      />
                    )}
                  />
                </Stack>
                <Text color="gray.600" fontSize="sm" mt={2}>
                  Mark this if this is your highest level of education
                </Text>
              </FormControl>

              {/* Course Duration */}
              <Stack direction={{ base: "column", md: "row" }} spacing={4} mt={4}>
                <FormControl
                  isInvalid={!!errors.course_duration_start_year}
                  isRequired
                  width={{ base: "100%", md: "50%" }}
                >
                  <FormLabel fontWeight="semibold">Starting Year</FormLabel>
                  <Select
                    {...register("course_duration_start_year", {
                      required: "Start year is required",
                      onChange: (e) => {
                        const newStartYear = Number(e.target.value);
                        const currentEnd = Number(watch("course_duration_end_year"));
                        if (!currentEnd || currentEnd < newStartYear) {
                          setValue("course_duration_end_year", newStartYear);
                        }
                        trigger("course_duration_start_year");
                        trigger("course_duration_end_year");
                      },
                    })}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={
                      errors.course_duration_start_year ? "red.500" : "gray.200"
                    }
                    _focus={{
                      borderColor: errors.course_duration_start_year
                        ? "red.500"
                        : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  >
                    {startYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errors.course_duration_start_year &&
                      errors.course_duration_start_year.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl
                  isInvalid={!!errors.course_duration_end_year}
                  isRequired
                  width={{ base: "100%", md: "50%" }}
                >
                  <FormLabel fontWeight="semibold">Completion Year</FormLabel>
                  <Select
                    {...register("course_duration_end_year", {
                      required: "Completion year is required",
                      validate: (value) => {
                        const sYear = Number(watch("course_duration_start_year"));
                        const eYear = Number(value);
                        if (Number.isNaN(eYear))
                          return "Please select a valid year";
                        if (eYear < sYear)
                          return "Completion year must be same as or after start year";
                        return true;
                      },
                    })}
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={
                      errors.course_duration_end_year ? "red.500" : "gray.200"
                    }
                    _focus={{
                      borderColor: errors.course_duration_end_year
                        ? "red.500"
                        : "blue.500",
                      boxShadow: "0 0 0 1px",
                    }}
                  >
                    {endYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errors.course_duration_end_year &&
                      errors.course_duration_end_year.message}
                  </FormErrorMessage>
                </FormControl>
              </Stack>

              {/* Navigation Buttons */}
              <Box textAlign="center" mt={8}>
                {Object.keys(errors).length > 0 && (
                  <Alert status="warning" mb={4} borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold">Please fix the following errors:</Text>
                      <Text fontSize="sm" mt={1}>
                        {Object.keys(errors)
                          .map((key) => errors[key]?.message)
                          .filter(Boolean)
                          .join(", ")}
                      </Text>
                    </Box>
                  </Alert>
                )}

                <Stack
                  direction={{ base: "column", sm: "row" }}
                  spacing={4}
                  justifyContent="center"
                >
                  <Button
                    leftIcon={<ArrowLeftIcon />}
                    colorScheme="gray"
                    onClick={handlePrevious}
                    width={{ base: "full", sm: "auto" }}
                    minW="150px"
                    isDisabled={isSubmitting}
                  >
                    Previous
                  </Button>
                  <Button
                    rightIcon={<ArrowRightIcon />}
                    type="submit"
                    colorScheme="blue"
                    isLoading={isSubmitting}
                    loadingText="Saving..."
                    width={{ base: "full", sm: "auto" }}
                    minW="150px"
                  >
                    Next
                  </Button>
                </Stack>
              </Box>
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
